import { Routes, Route } from "react-router-dom";
import { AdminSidebar } from "@/components/AdminSidebar";
import { DashboardHeader } from "@/components/DashboardHeader";
import { DashboardFooter } from "@/components/DashboardFooter";
import { AdminClientsTable } from "@/components/admin/AdminClientsTable";
import { ClientDetails } from "@/components/admin/client-details/ClientDetails";
import { ClientForm } from "@/components/admin/ClientForm";
import { DocumentCenter } from "@/components/admin/DocumentCenter";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { ClientPaymentOverview } from "@/components/admin/payment-tracking/ClientPaymentOverview";
import AdminNotifications from "@/pages/AdminNotifications";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardStats } from "@/components/admin/dashboard/DashboardStats";
import AdminMetrics from "@/pages/AdminMetrics";
import { AllClientsView } from "@/components/admin/AllClientsView";

const renderPlaceholder = (title: string) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] bg-[#222222] border border-white/10 rounded-lg">
      <h2 className="text-2xl font-semibold text-white mb-4">{title}</h2>
      <p className="text-gray-400">This feature is coming soon.</p>
    </div>
  );
};

const AdminDashboard = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalClients: 0,
    totalCalculations: 0,
    totalDocuments: 0
  });

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (clientsError) throw clientsError;

      const { count: calculationsCount, error: calcError } = await supabase
        .from('calculations')
        .select('*', { count: 'exact', head: true });

      if (calcError) throw calcError;

      const { count: documentsCount, error: docsError } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true });

      if (docsError) throw docsError;

      setClients(clientsData || []);
      setStats({
        totalClients: clientsData?.length || 0,
        totalCalculations: calculationsCount || 0,
        totalDocuments: documentsCount || 0
      });
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message);
      toast({
        variant: "destructive",
        title: "Error fetching data",
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [toast]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-[#1A1F2C] dark:bg-[#0F1219]">
        <AdminSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <DashboardHeader />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-[#1A1F2C] dark:bg-[#0F1219] pt-20">
            <div className="container mx-auto px-6 py-8">
              <Routes>
                <Route path="/" element={
                  <div className="space-y-8">
                    <h1 className="text-3xl font-bold text-white">Welcome to Admin Dashboard</h1>
                    <DashboardStats stats={stats} />
                    <AdminClientsTable 
                      clients={clients}
                      isLoading={isLoading}
                      onViewDetails={(clientId) => {
                        // Handled by the table component
                      }}
                      onClientDeleted={fetchData}
                    />
                    <ClientPaymentOverview />
                  </div>
                } />
                <Route path="/all-clients" element={<AllClientsView />} />
                <Route path="/clients/new" element={<ClientForm mode="add" />} />
                <Route path="/clients/:id" element={<ClientDetails />} />
                <Route path="/clients/:id/edit" element={<ClientDetails mode="edit" />} />
                <Route path="/analytics" element={renderPlaceholder("Case Analytics")} />
                <Route path="/leads" element={renderPlaceholder("Lead Management")} />
                <Route path="/import" element={renderPlaceholder("Import HubSpot Data")} />
                <Route path="/documents" element={<DocumentCenter />} />
                <Route path="/metrics" element={<AdminMetrics />} />
                <Route path="/support" element={renderPlaceholder("Support Requests")} />
                <Route path="/notifications" element={<AdminNotifications />} />
                <Route path="/settings" element={renderPlaceholder("System Settings")} />
              </Routes>
            </div>
          </main>
          <DashboardFooter />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminDashboard;
